import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../utils/api';
import { 
  ArrowLeft, CheckCircle2, XCircle, AlertCircle, 
  TrendingUp, Shield, Target, Zap, BarChart3, Clock,
  Rocket, ChevronRight, Loader2, AlertTriangle, Info
} from 'lucide-react';

const TransitionWizard = () => {
  const navigate = useNavigate();
  const { botId } = useParams();
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [allocation, setAllocation] = useState(5);
  const [step, setStep] = useState(1);
  const [bot, setBot] = useState(null);

  useEffect(() => {
    loadData();
  }, [botId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [readinessRes, criteriaRes, botRes] = await Promise.all([
        api.get(`/transition/readiness/${botId}`),
        api.get('/transition/criteria'),
        api.get(`/bots/${botId}`)
      ]);
      
      setReadiness(readinessRes.data);
      setCriteria(criteriaRes.data);
      setBot(botRes.data);
    } catch (error) {
      console.error('Failed to load readiness data:', error);
      toast.error('Failed to load transition data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransition = async () => {
    setTransitioning(true);
    try {
      const response = await api.post(`/transition/go-live/${botId}?initial_allocation=${allocation}`);
      toast.success('Successfully transitioned to live trading!');
      navigate(`/bot/${botId}`);
    } catch (error) {
      console.error('Transition error:', error);
      toast.error(error.response?.data?.detail || 'Failed to transition to live trading');
    } finally {
      setTransitioning(false);
    }
  };

  const getCheckIcon = (passed) => {
    if (passed) return <CheckCircle2 className="w-5 h-5 text-green-400" />;
    return <XCircle className="w-5 h-5 text-red-400" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500/20 to-green-500/5';
    if (score >= 60) return 'from-yellow-500/20 to-yellow-500/5';
    return 'from-red-500/20 to-red-500/5';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F6BFF] mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Analyzing your trading performance...</p>
        </div>
      </div>
    );
  }

  const checks = readiness?.checks || {};
  const score = readiness?.score || 0;
  const isReady = readiness?.ready || false;
  const paperPerf = readiness?.paper_performance || {};

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Button
            data-testid="transition-back-btn"
            onClick={() => navigate(`/bot/${botId}`)}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Bot
          </Button>
          <span className="text-sm text-[#94A3B8]">Paper-to-Live Transition</span>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-4xl">
        {/* Page Title */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className="mb-8 text-center"
        >
          <h1 className="text-4xl font-semibold font-['Outfit'] mb-2">Transition Wizard</h1>
          <p className="text-[#94A3B8]">
            {bot?.strategy?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Bot
          </p>
        </motion.div>

        {/* Step Indicator */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-[#2F6BFF] text-white' : 'bg-[#1E293B] text-[#94A3B8]'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#2F6BFF]' : 'bg-[#1E293B]'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-2 text-sm text-[#94A3B8]">
            <span className={step >= 1 ? 'text-[#F5F7FA]' : ''}>Assessment</span>
            <span className="w-16" />
            <span className={step >= 2 ? 'text-[#F5F7FA]' : ''}>Configure</span>
            <span className="w-16" />
            <span className={step >= 3 ? 'text-[#F5F7FA]' : ''}>Confirm</span>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {/* Step 1: Assessment */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Readiness Score Card */}
              <Card className={`bg-gradient-to-br ${getScoreBg(score)} border-[#1E293B]`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold mb-1">Readiness Score</h2>
                      <p className="text-[#94A3B8]">Based on your paper trading performance</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-5xl font-bold ${getScoreColor(score)}`}>{score}</span>
                      <span className="text-2xl text-[#94A3B8]">/100</span>
                    </div>
                  </div>
                  <Progress value={score} className="mt-4 h-2" />
                </CardContent>
              </Card>

              {/* Criteria Checks */}
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="font-['Outfit']">Readiness Criteria</CardTitle>
                  <CardDescription>All criteria must be met for live trading</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Subscription Check */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.subscription?.passed)}
                      <div>
                        <p className="font-medium">Subscription</p>
                        <p className="text-sm text-[#94A3B8]">
                          Current: {checks.subscription?.current?.toUpperCase() || 'None'}
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm ${checks.subscription?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.subscription?.passed ? 'Pro/Elite Active' : 'Upgrade Required'}
                    </span>
                  </div>

                  {/* Trade Count */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.sufficient_trades?.passed)}
                      <div>
                        <p className="font-medium">Paper Trades</p>
                        <p className="text-sm text-[#94A3B8]">
                          Minimum {checks.sufficient_trades?.required} trades required
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.sufficient_trades?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.sufficient_trades?.value} / {checks.sufficient_trades?.required}
                    </span>
                  </div>

                  {/* Win Rate */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.win_rate?.passed)}
                      <div>
                        <p className="font-medium">Win Rate</p>
                        <p className="text-sm text-[#94A3B8]">
                          Minimum {checks.win_rate?.required}% required
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.win_rate?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.win_rate?.value}%
                    </span>
                  </div>

                  {/* Max Drawdown */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.max_drawdown?.passed)}
                      <div>
                        <p className="font-medium">Max Drawdown</p>
                        <p className="text-sm text-[#94A3B8]">
                          Must be under {checks.max_drawdown?.required}%
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.max_drawdown?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.max_drawdown?.value}%
                    </span>
                  </div>

                  {/* Profit Factor */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.profit_factor?.passed)}
                      <div>
                        <p className="font-medium">Profit Factor</p>
                        <p className="text-sm text-[#94A3B8]">
                          Minimum {checks.profit_factor?.required} required
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.profit_factor?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.profit_factor?.value}
                    </span>
                  </div>

                  {/* Sharpe Ratio */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.sharpe_ratio?.passed)}
                      <div>
                        <p className="font-medium">Sharpe Ratio</p>
                        <p className="text-sm text-[#94A3B8]">
                          Minimum {checks.sharpe_ratio?.required} required
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.sharpe_ratio?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.sharpe_ratio?.value}
                    </span>
                  </div>

                  {/* Trading Duration */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0D12]">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(checks.trading_duration?.passed)}
                      <div>
                        <p className="font-medium">Trading Duration</p>
                        <p className="text-sm text-[#94A3B8]">
                          Minimum {checks.trading_duration?.required} days
                        </p>
                      </div>
                    </div>
                    <span className={`text-sm font-mono ${checks.trading_duration?.passed ? 'text-green-400' : 'text-red-400'}`}>
                      {checks.trading_duration?.value} days
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Recommendation */}
              <Card className={`${isReady ? 'bg-green-500/10 border-green-500/50' : 'bg-yellow-500/10 border-yellow-500/50'}`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-3">
                    {isReady ? (
                      <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <h3 className={`font-semibold ${isReady ? 'text-green-400' : 'text-yellow-400'}`}>
                        {isReady ? 'Ready for Live Trading' : 'Not Ready Yet'}
                      </h3>
                      <p className="text-[#94A3B8] mt-1">{readiness?.recommendation}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex justify-end">
                <Button
                  data-testid="transition-step1-next"
                  onClick={() => isReady && setStep(2)}
                  disabled={!isReady}
                  className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Configure */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="font-['Outfit']">Configure Live Trading</CardTitle>
                  <CardDescription>Set your initial allocation for live trading</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Allocation Slider */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Initial Allocation</label>
                      <span className="text-[#2F6BFF] font-bold">{allocation}%</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="25"
                      value={allocation}
                      onChange={(e) => setAllocation(Number(e.target.value))}
                      className="w-full h-2 bg-[#1E293B] rounded-lg appearance-none cursor-pointer accent-[#2F6BFF]"
                    />
                    <div className="flex justify-between text-xs text-[#94A3B8] mt-1">
                      <span>Conservative (1%)</span>
                      <span>Aggressive (25%)</span>
                    </div>
                  </div>

                  {/* Risk Warning */}
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-400">Risk Warning</p>
                        <p className="text-[#94A3B8] mt-1">
                          Live trading involves real money. We recommend starting with a small allocation 
                          (5% or less) and gradually increasing as you gain confidence.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      onClick={() => setAllocation(3)}
                      className={`p-4 rounded-lg border text-center transition-all ${
                        allocation <= 5 ? 'border-green-500/50 bg-green-500/10' : 'border-[#1E293B] hover:border-[#2F6BFF]/50'
                      }`}
                    >
                      <Shield className="w-6 h-6 mx-auto mb-2 text-green-400" />
                      <p className="font-medium text-sm">Conservative</p>
                      <p className="text-xs text-[#94A3B8]">3% allocation</p>
                    </button>
                    <button
                      onClick={() => setAllocation(10)}
                      className={`p-4 rounded-lg border text-center transition-all ${
                        allocation > 5 && allocation <= 15 ? 'border-[#2F6BFF]/50 bg-[#2F6BFF]/10' : 'border-[#1E293B] hover:border-[#2F6BFF]/50'
                      }`}
                    >
                      <Target className="w-6 h-6 mx-auto mb-2 text-[#2F6BFF]" />
                      <p className="font-medium text-sm">Balanced</p>
                      <p className="text-xs text-[#94A3B8]">10% allocation</p>
                    </button>
                    <button
                      onClick={() => setAllocation(20)}
                      className={`p-4 rounded-lg border text-center transition-all ${
                        allocation > 15 ? 'border-red-500/50 bg-red-500/10' : 'border-[#1E293B] hover:border-[#2F6BFF]/50'
                      }`}
                    >
                      <Zap className="w-6 h-6 mx-auto mb-2 text-red-400" />
                      <p className="font-medium text-sm">Aggressive</p>
                      <p className="text-xs text-[#94A3B8]">20% allocation</p>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-[#1E293B]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  data-testid="transition-step2-next"
                  onClick={() => setStep(3)}
                  className="bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                >
                  Review <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="font-['Outfit']">Confirm Transition</CardTitle>
                  <CardDescription>Review and confirm your live trading setup</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="p-4 rounded-lg bg-[#0B0D12] space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Bot</span>
                      <span className="font-medium">{bot?.strategy?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Trading Mode</span>
                      <span className="font-medium text-green-400">LIVE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Allocation</span>
                      <span className="font-medium">{allocation}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#94A3B8]">Readiness Score</span>
                      <span className={`font-medium ${getScoreColor(score)}`}>{score}/100</span>
                    </div>
                  </div>

                  {/* Paper Performance Summary */}
                  <div className="p-4 rounded-lg bg-[#0B0D12]">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-[#2F6BFF]" />
                      Paper Trading Performance
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[#94A3B8]">Total P&L:</span>
                        <span className={`ml-2 font-medium ${paperPerf.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${paperPerf.total_pnl?.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#94A3B8]">Win Rate:</span>
                        <span className="ml-2 font-medium">{paperPerf.win_rate}%</span>
                      </div>
                      <div>
                        <span className="text-[#94A3B8]">Trades:</span>
                        <span className="ml-2 font-medium">{paperPerf.total_trades}</span>
                      </div>
                      <div>
                        <span className="text-[#94A3B8]">Days Active:</span>
                        <span className="ml-2 font-medium">{paperPerf.days_trading}</span>
                      </div>
                    </div>
                  </div>

                  {/* Final Warning */}
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-red-400">Important Notice</p>
                        <p className="text-[#94A3B8] mt-1">
                          By clicking "Go Live", you authorize this bot to execute real trades on your 
                          connected exchange. Past paper trading performance does not guarantee future results.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-[#1E293B]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  data-testid="transition-confirm-btn"
                  onClick={handleTransition}
                  disabled={transitioning}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                >
                  {transitioning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Transitioning...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Go Live
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TransitionWizard;
